import { t } from "@/lib/i18n"

/**
 * Shape, endpoint and copy lookup for returning money on a settled payment.
 *
 * **The browser decides nothing about a refund.** Whether one is allowed and
 * how much is left to give back are answered by `Location::RefundEligibility`
 * on the server, asked once by `Staff::BillSerializer` for this screen and
 * again by `Location::RefundIssueService` for the write. This module reads
 * those answers; it never adds, subtracts or scales them. The rule is the one
 * the till screen already states: a figure the restaurant will argue about
 * later has to have exactly one author.
 *
 * ## The two rails read differently because they *are* different
 *
 * `provider` reverses a charge Stripe holds — the money goes back to the card
 * and nobody at the restaurant touches anything. `drawer` means a person opens
 * a till and counts notes into a diner's hand, out of *today's* drawer, and the
 * shift's arqueo is short by that much at close. One is a request; the other is
 * a physical act with a witness. Showing them with the same words would tell a
 * cashier that handing over 400 pesos is the same gesture as clicking a button,
 * so the dialog says which one is about to happen.
 */

export type RefundRail = "provider" | "drawer"

/**
 * `pending` belongs to the provider rail alone, and only while the Stripe call
 * is in flight. A drawer refund is never pending: the notes are in the diner's
 * hand or they are not.
 */
export type RefundStatus = "pending" | "succeeded" | "failed"

/** `settledPayments[].refundEligibility`, asked with no amount attached. */
export type RefundEligibility = {
  allowed: boolean
  /** What is still refundable on this charge, in integer cents, decided server-side. */
  refundableCents: number
  rail: RefundRail
  /** Why a refund is refused. `null` when it is allowed. */
  reasonCode: string | null
}

/** A row of `settledPayments[].refunds` — the ledger, failures included. */
export type IssuedRefund = {
  id: number
  status: RefundStatus
  reason: string
  reasonLabel: string
  /** Exact decimal string, the way every other amount on this screen arrives. */
  amount: string
  actorName: string | null
  /** What the provider said when it refused. `null` on anything that did not fail. */
  failureReason?: string | null
  createdAt: string
  completedAt?: string | null
}

export type RefundReasonOption = {
  value: string
  label: string
}

/** The one write this feature performs, nested under the charge it reverses. */
export const REFUND_PATHS = {
  create: (paymentAttemptId: number) => `/payment_attempts/${paymentAttemptId}/refunds`,
} as const

/** The location feature that has to be on for the endpoint to answer at all. */
export const REFUNDS_FEATURE = "refunds"

const I18N_PREFIX = "ui.react.features.staff.refunds"

export function tRefund(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/**
 * The drawer-rail refusal a cashier can actually do something about.
 *
 * `Location::RefundEligibility` does not report it — it knows nothing about
 * shifts — so today it arrives only as the flash alert the service raises on
 * submit. The mapping lives here anyway so that the day the payload can say
 * "no hay caja abierta" up front, the screen already explains it instead of
 * letting someone promise a diner cash the till cannot produce.
 */
export const NEEDS_OPEN_SHIFT = "needs_open_shift"

/**
 * Refusals worth naming. Everything else — `invalid_reason`, `exceeds_charge`,
 * `non_positive` — describes a request this screen cannot make in the first
 * place (the reason comes from a closed select and the amount is the server's
 * own figure), so it falls through to the generic line rather than being spelled
 * out in copy maintained for a state nobody can reach.
 */
const BLOCKED_KEYS: Record<string, string> = {
  not_settled: "blocked_not_settled",
  fully_refunded: "blocked_fully_refunded",
  [NEEDS_OPEN_SHIFT]: "drawer_no_shift",
}

export function refundBlockedKey(reasonCode: string | null | undefined): string {
  if (!reasonCode) return "blocked_generic"

  return BLOCKED_KEYS[reasonCode] ?? "blocked_generic"
}

const STATUS_KEYS: Record<string, string> = {
  pending: "issued_pending",
  succeeded: "issued_succeeded",
  failed: "issued_failed",
}

/**
 * An unrecognised status renders verbatim instead of being flattened into one
 * of the three we know: a refund whose state this build cannot name is exactly
 * the row a manager needs to see as odd.
 */
export function refundStatusLabel(status: string): string {
  const key = STATUS_KEYS[status]

  return key ? tRefund(key) : status
}

/**
 * Cents to major units, for `formatMoney`.
 *
 * A unit conversion for display — the same class of operation as putting a
 * currency symbol on a figure — not a derived total. The server decided
 * `refundableCents`; this screen shows that number and no other.
 */
export function refundDisplayAmount(cents: number): number {
  return Number.isFinite(cents) ? cents / 100 : 0
}

/**
 * The same figure again, as the decimal string the endpoint parses.
 *
 * `Staff::Location::RefundsController` takes `amount`, and
 * `Location::RefundIssueService` turns it straight back into cents to derive
 * the idempotency key — so this has to round-trip exactly. Built with integer
 * arithmetic rather than `(cents / 100).toFixed(2)` so no binary-float dust can
 * ever land a centavo away from what the server offered, which on this endpoint
 * would be a different idempotency key and therefore a second refund.
 */
export function refundAmountParam(cents: number): string {
  if (!Number.isFinite(cents)) return "0.00"

  const whole = Math.trunc(Math.abs(cents))
  const sign = cents < 0 ? "-" : ""

  return `${sign}${Math.floor(whole / 100)}.${String(whole % 100).padStart(2, "0")}`
}
